import type { Auth } from 'firebase/auth';
import {
  type Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';

import { nameTokens } from '../../core/primitives/nameSearch';
import {
  EmployeeAdminError,
  type EmployeeAdminPort,
  type EmployeeCorrection,
} from '../../features/committee/application/ports/employeeAdminPort';

/**
 * Firestore implementation of `EmployeeAdminPort` (blueprint §11.1).
 *
 * A field-level update rather than a whole-document write: the rules let the
 * committee change any field, so replacing the document would silently drop
 * whatever this screen has not been taught about — enrolment, coverage, the
 * timestamps the employee's own screens maintain.
 */
export function createFirebaseEmployeeAdminAdapter(
  firestore: Firestore,
  auth: Auth,
): EmployeeAdminPort {
  return {
    async getEmployee(employeeId): Promise<EmployeeCorrection | null> {
      const found = await getDoc(doc(firestore, 'employees', employeeId));
      if (!found.exists()) return null;

      const data = found.data();
      const readString = (key: string): string | null => {
        const value: unknown = data[key];
        return typeof value === 'string' && value !== '' ? value : null;
      };
      const children: unknown = data['declaredChildrenCount'];

      const married: unknown = data['declaredMaritalStatus'];

      return {
        fullName: readString('fullName') ?? '',
        branchId: readString('branchId'),
        workEmail: readString('workEmail'),
        phone: readString('phone'),
        address: readString('address'),
        declaredChildrenCount: typeof children === 'number' ? children : 0,
        declaredMaritalStatus: typeof married === 'boolean' ? married : null,
      };
    },

    async correctEmployee(employeeId, correction): Promise<void> {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new EmployeeAdminError('forbidden', 'يجب تسجيل الدخول أولًا.');

      // The author comes from the verified link. The rules require `updatedBy`
      // to equal the caller's own employee id, so a forged one is refused at
      // the boundary too (§4.2).
      const link = await getDoc(doc(firestore, 'userAccounts', uid));
      const editorId: unknown = link.data()?.['employeeId'];

      if (typeof editorId !== 'string') {
        throw new EmployeeAdminError('forbidden', 'لا يوجد سجل موظف مرتبط بحسابك.');
      }

      /*
       * One address, one record.
       *
       * The address decides who is linked to this record on their first
       * sign-in. Two records carrying it would hand the account to whichever
       * the lookup happened to read first, so the collision is refused here
       * rather than discovered by the wrong person getting in.
       */
      if (correction.workEmail !== null) {
        const claimed = await getDocs(
          query(
            collection(firestore, 'employees'),
            where('workEmail', '==', correction.workEmail),
            limit(2),
          ),
        );

        if (claimed.docs.some((entry) => entry.id !== employeeId)) {
          throw new EmployeeAdminError(
            'email_taken',
            'هذا البريد مسجَّل على موظف آخر في الكشف.',
          );
        }
      }

      try {
        await updateDoc(doc(firestore, 'employees', employeeId), {
          fullName: correction.fullName,
          /*
           * Re-indexed with the name, in the same write.
           *
           * The committee searches by these tokens. Correcting a spelling and
           * leaving them behind means the search keeps finding the person by
           * the name that was wrong and stops finding them by the right one —
           * and nothing would say so.
           */
          nameTokens: nameTokens(correction.fullName),
          branchId: correction.branchId,
          workEmail: correction.workEmail,
          phone: correction.phone,
          address: correction.address,
          declaredChildrenCount: correction.declaredChildrenCount,
          declaredMaritalStatus: correction.declaredMaritalStatus,
          updatedAt: new Date().toISOString(),
          updatedBy: editorId,
        });
      } catch (error) {
        const code =
          typeof error === 'object' && error !== null && 'code' in error
            ? String(error.code)
            : '';

        if (code.includes('permission-denied')) {
          throw new EmployeeAdminError('forbidden', 'لا تملك صلاحية تعديل سجلات الموظفين.');
        }
        if (code.includes('not-found')) {
          throw new EmployeeAdminError('not_found', 'لم يُعثر على سجل الموظف.');
        }
        throw new EmployeeAdminError('unknown', 'تعذّر حفظ التعديل.');
      }
    },
  };
}
